import type {
  LiveQuoteReloadFormRequest,
  LiveReferentialFormResponse,
  LiveTradeAmendRequest,
  LiveTradeResponse,
} from 'apiConnector/secondary-market/live/models';
import type {
  SecondaryPostEventsLivePersistedTabContent,
  SecondaryQuotesLiveFormPersistedTabContent,
} from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { isSecondaryMarketQuotesLivePersistedTabContent } from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { liveAmendActions } from 'state/secondaryMarket/live/amend/amend.actions';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveOriginalFormActions } from 'state/secondaryMarket/live/form/original/liveOriginalForm.actions';
import { liveOriginalFormSelectors } from 'state/secondaryMarket/live/form/original/liveOriginalForm.selector';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import { secondaryMarketThunksFactory, tabCreatedThunksFactory } from 'state/secondaryMarket/shared/thunk';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import { fromSecondaryPostEventsWorkspace } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { workspaceTabFrozen } from 'state/workspaces/workspace.actions';

import { liveInputFormActions } from './form/input/inputForm.actions';
import type { LiveFormField } from './form/input/inputForm.model';
import { getLiveQuoteUpfront } from './quote/liveQuote.selector';

const thunks = secondaryMarketThunksFactory<
  LiveTradeAmendRequest,
  LiveTradeResponse,
  LiveReferentialFormResponse,
  LiveQuoteReloadFormRequest,
  SecondaryQuotesLiveFormPersistedTabContent | SecondaryPostEventsLivePersistedTabContent
>('LIVE', 'live', liveSelectors);
const tabCreatedThunks = tabCreatedThunksFactory('LIVE', 'live', (isinOrFileCode: string) => `Live ${isinOrFileCode}`);
const amendRequestedThunk = (tabId: string): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const etag = liveOriginalFormSelectors.getEtag(state, tabId);
    const request = liveSelectors.getAmendRequest(state, tabId);
    const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
    if (etag !== undefined) {
      dispatch(liveAmendActions.secondaryMarketAmendRequested(tabId, orderId, etag, request));
      dispatch(workspaceTabFrozen(tabId, 'secondaryMarketPostEvents', true));
    }
  };
};

const reloadTabThunk = (tabId: string): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const tabContent = liveFormInputSelectors.getFormTabContentToPersist(state, tabId);
    if (!tabContent || !isSecondaryMarketQuotesLivePersistedTabContent(tabContent)) {
      return;
    }

    const request: LiveQuoteReloadFormRequest = {
      productKeyRequest: toBcsProductKey(tabContent?.bcsProductKey),
      clientType: tabContent.clientType,
      client: tabContent.client,
      custodian: tabContent.custodian,
      settlementDate: tabContent.settlementDate,
      salesId: tabContent.salesId,
    };
    dispatch(liveInputFormActions.reloadFormFetchRequested(tabId, 'secondaryMarketQuotes', request));
  };
};
const amendFetchedThunk = (tabId: string, tradeId: string, response: LiveTradeResponse): Thunk<void> => {
  return (dispatch, getState) => {
    dispatch(liveOriginalFormActions.secondaryMarketAmendFetched(tabId, tradeId, response));
    const state = getState();
    const originalClient = liveOriginalFormSelectors.getFormClient(state, tabId);
    const originalClientType = liveOriginalFormSelectors.getFormClientType(state, tabId);
    const loadReferentialRequest: LiveQuoteReloadFormRequest = {
      productKeyRequest: response.bcsKey,
      clientType: originalClientType,
      client: originalClient,
      custodian: liveOriginalFormSelectors.getFormCustodian(state, tabId),
      salesId: liveOriginalFormSelectors.getFormSalesId(state, tabId),
      settlementDate: liveOriginalFormSelectors.getFormSettlementDate(state, tabId),
      tradeWay: liveOriginalFormSelectors.getFormTradeWay(state, tabId),
    };
    dispatch(liveInputFormActions.reloadFormFetchRequested(tabId, 'secondaryMarketPostEvents', loadReferentialRequest));
  };
};
const resetCustomizedUpfrontThunk = (tabId: string): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const defaultUpfront = getLiveQuoteUpfront(state, tabId);
    if (defaultUpfront !== undefined) {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'customizedUpfront', defaultUpfront));
    }
  };
};

const resetCustomizedXMarginThunk = (tabId: string): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const defaultUpfront = getLiveQuoteUpfront(state, tabId);
    if (defaultUpfront !== undefined) {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'customizedXMarginAdjustment', -defaultUpfront));
    }
  };
};
const percentFieldChangedThunk = <
  F extends Extract<
    LiveFormField,
    'customizedUpfront' | 'customizedTradingPriceAdjustment' | 'customizedXMarginAdjustment'
  >,
>(
  tabId: string,
  field: F,
  value: number | undefined,
): Thunk<void> => {
  return (dispatch, getState) => {
    if (value === undefined) {
      dispatch(liveInputFormActions.formFieldChanged(tabId, field, undefined));
      return;
    }

    const state = getState();
    const priceType = liveOriginalFormSelectors.getPriceType(state, tabId);
    if (priceType === 'Percent') {
      dispatch(liveInputFormActions.formFieldChanged(tabId, field, value / 100.0));
    } else {
      dispatch(liveInputFormActions.formFieldChanged(tabId, field, value));
    }
  };
};

export const liveThunks = {
  ...thunks,
  ...tabCreatedThunks,
  amendRequestedThunk,
  reloadTabThunk,
  amendFetchedThunk,
  resetCustomizedUpfrontThunk,
  resetCustomizedXMarginThunk,
  percentFieldChangedThunk,
};

export type LiveThunks = typeof liveThunks;
